import {
    AsYouType,
    CountryCode,
    formatIncompletePhoneNumber,
    getCountryCallingCode,
    isValidPhoneNumber,
    parsePhoneNumberFromString,
} from 'libphonenumber-js/max';

export type PhoneFormShape = {
    countryCode?: CountryCode;
    phoneNumber?: string;
};

/**
 * getNationalPhoneNumberForm
 * returns national formatted phonenumber without the international country prefix
 * from an incoming international formatted number (with the country code)
 * e.g. +1 (213) 373-4253 (US) or +688 77 79797 (Tuvalu)
 * @param phoneNumber string
 * @param countryCode CountryCode optional
 * @returns PhoneFormShape
 */
export const getNationalPhoneNumberForm = (phoneNumber: string, countryCode?: CountryCode): PhoneFormShape => {
    if (!phoneNumber) {
        return {
            countryCode: 'US',
            phoneNumber: '',
        };
    }
    const handler = new AsYouType();
    handler.input('+' + phoneNumber);
    const cc = countryCode || handler.getCountry() || 'US';
    const ccRegex = new RegExp('^' + getCountryCallingCode(cc));
    const parsedIncomingNationalNumber = phoneNumber.replace(ccRegex, '');
    const parsed = handler.getNumber();
    const nationalNumber = parsed?.nationalNumber || parsedIncomingNationalNumber;
    return { countryCode: cc, phoneNumber: nationalNumber };
};

/**
 * getE164FormattedNumber
 * returns an E.164 formatted phone string, including international prefix
 * eg. +14158675309
 * if no countryCode is specified, a default of US is used.
 * blank string returned if number not successfully parsed
 * @param phoneNumber
 * @return string
 */
export const getE164FormattedNumber = (phoneNumber: PhoneFormShape): string => {
    if (!phoneNumber.phoneNumber) {
        return '';
    }
    let countryCode = phoneNumber.countryCode;
    if (!countryCode) {
        countryCode = 'US';
    }
    const parsed = parsePhoneNumberFromString(phoneNumber.phoneNumber, countryCode);
    if (parsed) {
        return parsed.format('E.164');
    }
    return '';
};

/**
 * validatePhone - validates incoming PhoneFormShape
 * returns false if phoneNumber or countryCode elements are empty
 * @param phoneForm
 * @returns boolean
 */
export const validatePhone = (phoneForm: PhoneFormShape): boolean => {
    if (!phoneForm.phoneNumber || !phoneForm.countryCode) {
        return false;
    }
    const callingCode = getCountryCallingCode(phoneForm.countryCode);
    const checkNumber = `${callingCode}${phoneForm.phoneNumber}`;
    return isValidPhoneNumber(checkNumber, phoneForm.countryCode);
};

/**
 * returns a formatted phone number from incoming string
 * @param phoneNumber
 * @returns string formatted phone number
 */
export const getFormattedPhoneNumber = (phoneNumber = ''): string => {
    let cleanPhoneNumber = phoneNumber?.replace(/\D/g, '');
    const isUSNumber = cleanPhoneNumber.length === 11 && cleanPhoneNumber.charAt(0) === '1';
    if (isUSNumber) {
        cleanPhoneNumber = `+${cleanPhoneNumber}`;
        const parsed = parsePhoneNumberFromString(cleanPhoneNumber);
        if (parsed) {
            return parsed.formatNational();
        }
    } else {
        cleanPhoneNumber = `+${cleanPhoneNumber}`;
        const parsed = parsePhoneNumberFromString(cleanPhoneNumber);
        if (parsed) {
            return parsed.formatInternational();
        }
    }
    return phoneNumber;
};

/**
 * getInternationalFormattedPhoneNumber
 * provides an formatted phone number with international prefix from incoming PhoneFormShape
 * eg. +1 (213) 373-4253
 * @param phoneNumber PhoneFormShape
 * @returns string
 */
export const getInternationalFormattedPhoneNumber = (phoneNumber: PhoneFormShape): string => {
    if (!phoneNumber.phoneNumber || !phoneNumber.countryCode) {
        return '';
    }
    if (phoneNumber.phoneNumber.charAt(0) === '+') {
        return phoneNumber.phoneNumber;
    }
    const formattedNum = formatIncompletePhoneNumber(phoneNumber.phoneNumber, phoneNumber.countryCode);
    return `+${getCountryCallingCode(phoneNumber.countryCode)} ${formattedNum}`;
};
